import { memo, useMemo } from 'react'

import { DemoHidden } from '@/features/demo/components/demo-hidden'
import { DemoModeIndicator } from '@/features/demo/components/demo-mode-indicator'

import { useUserTenants } from '@/shared/auth/hooks/use-user-tenants'
import { useCurrentRole, useCurrentTenant } from '@/shared/auth/store'

import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { Skeleton } from '@/components/ui/skeleton'

import { Breadcrumbs } from './app-breadcrumbs'

/**
 * App shell header: sidebar trigger, breadcrumbs, and the active
 * tenant name + current role aligned to the right.
 */
export const AppHeader = memo(() => {
	const allowed_tenants = useUserTenants()
	const tenant = useCurrentTenant()
	const role = useCurrentRole()

	const tenantName = useMemo(() => {
		return allowed_tenants.length > 0
			? (allowed_tenants.find((t) => t.id === tenant)?.fullname ?? '??')
			: tenant
	}, [allowed_tenants, tenant])

	return (
		<header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
			<SidebarTrigger />
			<Separator orientation="vertical" className="mr-2 h-4" />
			<Breadcrumbs />

			<div className="flex-auto" />

			<DemoModeIndicator />

			<DemoHidden>
				<div className="hidden flex-col items-end text-sm sm:flex">
					<span className="truncate font-semibold">{tenantName}</span>
					<span className="truncate text-xs">{role}</span>
				</div>
			</DemoHidden>
		</header>
	)
})

export const HeaderSkeleton = () => {
	return (
		<header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
			<Skeleton className="size-8" />
			<Separator orientation="vertical" className="mr-2 h-4" />
			<Skeleton className="h-8 w-24" />

			<div className="flex-auto" />

			<div className="hidden flex-col items-end gap-1 text-sm sm:flex">
				<Skeleton className="h-6 w-24" />
				<Skeleton className="h-4 w-12" />
			</div>
		</header>
	)
}
